import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { 
  Search, 
  Users, 
  Printer, 
  Ticket, 
  Calendar, 
  FileText,
  Plus,
  Settings,
  BarChart3,
  Package,
  Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick navigation items
const quickNavItems = [
  { label: 'Dashboard', icon: BarChart3, path: '/', keywords: ['home', 'panoramica'] },
  { label: 'Clienti', icon: Users, path: '/clienti', keywords: ['anagrafica', 'rubrica'] },
  { label: 'Impianti', icon: Printer, path: '/impianti', keywords: ['registratori', 'stampanti', 'matricole'] },
  { label: 'Tickets', icon: Ticket, path: '/tickets', keywords: ['assistenza', 'richieste', 'problemi'] },
  { label: 'Calendario', icon: Calendar, path: '/calendario', keywords: ['appuntamenti', 'agenda'] },
  { label: 'Documenti', icon: FileText, path: '/documenti', keywords: ['preventivi', 'fatture'] },
  { label: 'Magazzino', icon: Package, path: '/magazzino', keywords: ['articoli', 'scorte', 'ricambi'] },
  { label: 'Impostazioni', icon: Settings, path: '/impostazioni', keywords: ['configurazione'] },
];

// Quick actions
const quickActions = [
  { label: 'Nuovo Cliente', icon: Plus, path: '/clienti/nuovo', keywords: ['crea', 'aggiungi'] },
  { label: 'Nuovo Ticket', icon: Plus, action: 'new-ticket', keywords: ['crea', 'apri'] },
];

export default function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    clienti: [],
    tickets: [],
    impianti: []
  });
  const [isSearching, setIsSearching] = useState(false);

  // Keyboard shortcut to open
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
      // Escape to close
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  // Debounced search
  const searchData = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ clienti: [], tickets: [], impianti: [] });
      return;
    }
    
    setIsSearching(true);
    try {
      const [clientiRes, ticketsRes, impiantiRes] = await Promise.all([
        axios.get(`${API}/clienti/search?q=${encodeURIComponent(query)}`).catch(() => ({ data: [] })),
        axios.get(`${API}/tickets`).catch(() => ({ data: [] })),
        axios.get(`${API}/impianti`).catch(() => ({ data: [] }))
      ]);

      // Filter tickets and impianti locally
      const queryLower = query.toLowerCase();
      const filteredTickets = ticketsRes.data.filter(t => 
        t.oggetto?.toLowerCase().includes(queryLower) ||
        t.descrizione?.toLowerCase().includes(queryLower) ||
        String(t.numero).includes(query)
      ).slice(0, 5);

      const filteredImpianti = impiantiRes.data.filter(i =>
        i.matricola?.toLowerCase().includes(queryLower) ||
        i.tipo?.toLowerCase().includes(queryLower) ||
        i.modello?.toLowerCase().includes(queryLower)
      ).slice(0, 5);

      setSearchResults({
        clienti: clientiRes.data.slice(0, 5),
        tickets: filteredTickets,
        impianti: filteredImpianti
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchData(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchData]);

  const handleSelect = (path) => {
    onOpenChange(false);
    setSearchQuery('');
    navigate(path);
  };

  const handleAction = (action) => {
    onOpenChange(false);
    setSearchQuery('');
    if (action === 'new-ticket') {
      // Dispatch custom event to open ticket dialog
      window.dispatchEvent(new CustomEvent('open-new-ticket'));
      navigate('/tickets');
    }
  };

  const hasResults = searchResults.clienti.length > 0 || 
                     searchResults.tickets.length > 0 || 
                     searchResults.impianti.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Cerca clienti, tickets, impianti... (Ctrl+K)" 
        value={searchQuery}
        onValueChange={setSearchQuery}
        data-testid="global-search-input"
      />
      <CommandList>
        {isSearching && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Ricerca in corso...
          </div>
        )}

        {!isSearching && searchQuery.length >= 2 && !hasResults && (
          <CommandEmpty>Nessun risultato per "{searchQuery}"</CommandEmpty>
        )}

        {/* Search Results */}
        {searchResults.clienti.length > 0 && (
          <CommandGroup heading="Clienti">
            {searchResults.clienti.map((cliente) => (
              <CommandItem
                key={cliente.id}
                value={`cliente-${cliente.id}`}
                onSelect={() => handleSelect(`/clienti/${cliente.id}`)}
                className="cursor-pointer"
              >
                <Users className="mr-2 h-4 w-4 text-cyan-500" />
                <div className="flex flex-col">
                  <span className="font-medium">{cliente.ragione_sociale}</span>
                  <span className="text-xs text-muted-foreground">
                    {cliente.partita_iva} • {cliente.citta}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {searchResults.tickets.length > 0 && (
          <CommandGroup heading="Tickets">
            {searchResults.tickets.map((ticket) => (
              <CommandItem
                key={ticket.id}
                value={`ticket-${ticket.id}`}
                onSelect={() => handleSelect(`/tickets/${ticket.id}`)}
                className="cursor-pointer"
              >
                <Ticket className="mr-2 h-4 w-4 text-orange-500" />
                <div className="flex flex-col">
                  <span className="font-medium">#{ticket.numero} - {ticket.oggetto}</span>
                  <span className="text-xs text-muted-foreground">
                    {ticket.stato} • {ticket.priorita}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {searchResults.impianti.length > 0 && (
          <CommandGroup heading="Impianti">
            {searchResults.impianti.map((impianto) => (
              <CommandItem
                key={impianto.id}
                value={`impianto-${impianto.id}`}
                onSelect={() => handleSelect(`/impianti/${impianto.id}`)}
                className="cursor-pointer"
              >
                <Printer className="mr-2 h-4 w-4 text-green-500" />
                <div className="flex flex-col">
                  <span className="font-medium">{impianto.matricola}</span>
                  <span className="text-xs text-muted-foreground">
                    {impianto.tipo} • {impianto.modello}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading="Azioni Rapide">
          {quickActions.map((item) => (
            <CommandItem
              key={item.label}
              value={item.label}
              onSelect={() => item.path ? handleSelect(item.path) : handleAction(item.action)}
              className="cursor-pointer"
            >
              <item.icon className="mr-2 h-4 w-4 text-primary" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Navigation */}
        <CommandGroup heading="Navigazione">
          {quickNavItems.map((item) => (
            <CommandItem
              key={item.path}
              value={item.label + ' ' + item.keywords.join(' ')}
              onSelect={() => handleSelect(item.path)}
              className="cursor-pointer"
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
